import React, { ReactNode } from 'react';
import { ContentSkeleton } from '@cp/ds/src/components/contentSkeleton';
import { Box } from '@cp/ds/src/components/box';
import { Typography } from '@cp/ds/src/components/typography';
import { MapPointIcon } from '@cp/ds/src/icons';
import { Divider } from '@cp/ds/src/components/divider';
import { CatalogItemViewModel } from '@common/model/catalog/item/view';
import { SxProps } from '@cp/ds';
import { formatCatalogItemDate } from '@cp/entities/catalogItem';
import { Schema } from '@cp/ds/src/components/schema';
import { useConfig } from '@cp/shared/config';
import { useTitle } from '@cp/entities/vacancy';
import { createSchema, useAddress, useReplies } from '../model';
import { Parameters } from './card/parameters';
import { Author } from './author';

export interface VacancyViewProps {
  data: CatalogItemViewModel | undefined;
  map?: ReactNode;
  isLoading?: boolean;
  sx?: SxProps;
}

export const VacancyView: React.FC<VacancyViewProps> = ({ data, map, isLoading, sx }) => {
  const title = useTitle(data);
  const address = useAddress(data);
  const replies = useReplies(data);
  const config = useConfig();

  if (isLoading) {
    return <ContentSkeleton blocks={2} />;
  }

  if (!data) {
    return null;
  }

  return (
    <Box sx={sx}>
      <Schema schema={createSchema(data, title, config)} />

      <Typography component="h1" sx={{ my: 0, mr: 2.5, p: 0, typography: { xs: 'h3', md: 'h2' } }}>
        {title}
      </Typography>

      <Typography component="div" sx={{ my: 1.5, display: 'flex', alignItems: 'center' }} variant="body bold">
        <MapPointIcon fontSize="1.25rem" />
        <Typography sx={{ color: 'gray dark' }}>{address}</Typography>
      </Typography>

      <Parameters
        childrenCountProps={{ sx: { color: 'gray dark', typography: 'body bold' }, iconSx: { color: 'brand gray' } }}
        data={data}
      />

      <Author data={data} sx={{ mt: 1.5 }} />

      <Divider sx={{ my: 3.75 }} />

      <Typography component="div" variant="body">
        {data.translation?.description ?? ''}
      </Typography>

      <Box sx={{ my: 3.75 }}>{map}</Box>

      {replies !== null && (
        <>
          <Divider />

          <Typography component="div" sx={{ mt: 1.5 }} variant="body bold">
            {replies}
          </Typography>
        </>
      )}

      <Divider sx={{ my: 3.75 }} />

      <Typography sx={{ color: 'gray' }} variant="body">
        Дата создания: {formatCatalogItemDate(new Date(data.createdAt))}
      </Typography>
    </Box>
  );
};
